import { useMemo, useState, useEffect } from 'react';
import loadable from '@loadable/component';
import { Column } from '@simplywallst/ui-core';
import Statement from '@/components/Statement';
import Description from '@/components/Statement/components/Description';
import type { BalanceSheetHierarchyNode } from '../BalanceSheet';
import { Button } from '@simplywallst/ui-core';
import ReportSection from '@/components/ReportSection';
import ReportSubSection from '@/components/ReportSubSection';
import track, { useTracking } from 'react-tracking';
import {
  COMPANY_DEBT_TO_EQUITY_HISTORY,
  COMPANY_FINANCIAL_POSITION_ANALYSIS,
} from '@/constants/feedbackSections';
import { ChartType } from '@/constants/downloadSections';
import { getCompanyInfographicDownloadUrl } from '@utilities/urlGenerator';
import {
  HEALTH_BALANCE_SHEET,
  HEALTH_CASH_RUNWAY,
  HEALTH_DEBT_HISTORY,
  HEALTH_FINANCIAL_INSTITUTION,
  HEALTH_FINANCIAL_POSITION,
  LegacyAnchor,
} from '@/constants/anchors';
import { CompanyStatements } from '@/constants/statementIds';
import PageAnchor from '@/components/PageAnchor';
/** Raw Data Containers */
import { BalanceSheetWrapper } from './styled';
import { BrazeContentCardWrapper } from '@/pages/CompanyReport/components/BrazeContentCard';
import CompanySectionTitle from '@/pages/CompanyReport/components/CompanySectionTitle';
import ChartWrapper from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import { useVisibleSectionsContext } from '../../../VisibleSectionsContextProvider';
import useCompanyStatementsAPIFormatted from '@/hooks/useCompanyStatementsAPIFormatted';
import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import Header from '../Header';
import { SectionSchema } from '@/pages/CompanyReport/schemas';
import useExperimentPeerCompanies from '@/pages/CompanyReport/components/SimilarCompanies/useExperimentPeerCompanies';

const BrazeContentCard = loadable(() => import('@components/BrazeContentCard'));

const HealthNextStepsContainer = loadable(
  () => import('../../containers/HealthNextStepsContainer')
);

const BalanceSheet = loadable(() => import('../BalanceSheet'), { ssr: false });

const DebtToEquityHistoryRawDataContainer = loadable(
  () => import('../../containers/DebtToEquityHistoryRawDataContainer'),
  { ssr: false }
);

const DebtToEquityHistory = loadable(() => import('../DebtToEquityHistory'), {
  ssr: false,
});
const AssetsVsLiabilities = loadable(() => import('../AssetsVsLiabilities'), {
  ssr: false,
});

export interface Props {
  companyId: string;
  companyName: string;
  isFund: boolean;
  longTermAssets: number;
  longTermAssetsFormatted0DP: string;
  longTermAssetsFormatted2DP: string;
  longTermLiabilities: number;
  longTermLiabilitiesFormatted0DP: string;
  longTermLiabilitiesFormatted2DP: string;
  reportingCurrencyISO: string;
  reportingUnit: number;
  sectionNumber: number;
  shortTermAssets: number;
  shortTermAssetsFormatted0DP: string;
  shortTermAssetsFormatted2DP: string;
  shortTermLiabilities: number;
  shortTermLiabilitiesFormatted0DP: string;
  shortTermLiabilitiesFormatted2PD: string;
  /**
   * Debt To Equity History
   */
  debt: number[][];
  equity: number[][];
  cash: number[][];
  debtMax: number;
  equityMax: number;
  cashMax: number;
  debtMin: number;
  equityMin: number;
  cashMin: number;
  /**
   * Balance Sheet
   */
  liabilitiesBalanceSheet: BalanceSheetHierarchyNode;
  assetsBalanceSheet: BalanceSheetHierarchyNode;
}

const Health = ({
  companyId,
  isFund,
  reportingCurrencyISO,
  reportingUnit,
  sectionNumber,
  companyName,
  shortTermAssets,
  shortTermLiabilities,
  longTermAssets,
  longTermLiabilities,
  shortTermAssetsFormatted0DP,
  shortTermAssetsFormatted2DP,
  shortTermLiabilitiesFormatted0DP,
  shortTermLiabilitiesFormatted2PD,
  longTermAssetsFormatted0DP,
  longTermAssetsFormatted2DP,
  longTermLiabilitiesFormatted0DP,
  longTermLiabilitiesFormatted2DP,
  /**
   * Debt To Equity History
   */
  debt,
  equity,
  cash,
  debtMax,
  equityMax,
  cashMax,
  debtMin,
  equityMin,
  cashMin,
  /**
   * Balance Sheet
   */
  liabilitiesBalanceSheet,
  assetsBalanceSheet,
}: Props) => {
  const { isPristine } = useVisibleSectionsContext();
  const subSectionTitleElement = isPristine ? 'h2' : 'h3';
  const [showReport, setShowReport] = useState(true);
  const tracking = useTracking();
  const trackingButtonData = {
    action: 'click',
    modifier: 'show_Meaningless_data',
  };

  const canonicalUrl = useCompanyCanonicalUrl();
  const { data: statements } = useCompanyStatementsAPIFormatted({
    canonicalURL: canonicalUrl,
  });
  const {
    AreShortTermLiabilitiesCovered: areShortTermLiabilitiesCovered,
    AreLongTermLiabilitiesCovered: areLongTermLiabilitiesCovered,
    IsDebtLevelAppropriate: isDebtLevelAppropriate,
    HasDebtReducedOverTime: hasDebtReducedOverTime,
    IsDebtCoveredByCashflow: isDebtCoveredByCashflow,
    IsInterestCoveredByProfit: isInterestCoveredByProfit,
    HasAnAppropriateLevelOfAssets: hasAnAppropriateLevelOfAssets,
    HasAppropriateBadLoanAllowance: hasAppropriateBadLoanAllowance,
    HasPrimarilyLowRiskFunding: hasPrimarilyLowRiskFunding,
    HasAppropriateLoanLevel: hasAppropriateLoanLevel,
    HasPrimarilyDepositFunding: hasPrimarilyDepositFunding,
    HasAppropriateNonPerformingLoans: hasAppropriateNonPerformingLoans,
    HasCashRunwayIfStable: hasCashRunwayIfStable,
    HasCashRunwayIfGrowing: hasCashRunwayIfGrowing,
    IsProfitable: isProfitable,
    IsProfitableLongTerm: isProfitableLongTerm,
    IsHealthAnalysisMeaningless: isHealthAnalysisMeaningless,
    IsBank: isBankStatement,
  } = statements || {};

  const isDataMeaningless = isHealthAnalysisMeaningless?.value === true;
  const isBank = isBankStatement?.value === true;

  useEffect(() => {
    setShowReport(isDataMeaningless === false);
  }, [isDataMeaningless]);

  const { netWorthDownloadUrl, historicalDebtDownloadUrl } = useMemo(() => {
    return {
      netWorthDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.FinancialPositionAnalysis
      ),
      historicalDebtDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.DebtEquityHistoryAnalysis
      ),
    };
  }, [companyId]);

  const isLossMaking =
    isProfitable?.value === false && isProfitableLongTerm?.value === false;

  const showCashRunwayAnalysis = isLossMaking && !isBank;

  const nextStep = useExperimentPeerCompanies({
    originalElement: (
      <HealthNextStepsContainer
        companyId={companyId}
        isDataMeaningless={isDataMeaningless}
      />
    ),
    section: 'health',
  });

  return (
    <ReportSection sectionNumber={sectionNumber} data-cy-id="health-section">
      <SectionSchema section="health" />
      <CompanySectionTitle title={'Balance Sheet Health'} />
      <Header />
      {isDataMeaningless && (
        <ReportSubSection
          body={
            <>
              <p>
                In this section we usually analyse {companyName}
                's finance health to determine how well-positioned it is against
                times of financial distress, in particular, its ability to
                manage its cash and debt levels. {companyName} has not provided
                adequate balance sheet data, its financial health cannot be
                properly assessed as it.
              </p>
              <p>
                This is quite a rare situation as 97% of companies covered by
                Simply Wall St do have financial information.
              </p>
              {showReport === false && (
                <Button
                  styleType="louderLarge"
                  onClick={() => {
                    tracking.trackEvent(trackingButtonData);
                    setShowReport(!showReport);
                  }}
                >
                  Show section
                </Button>
              )}
            </>
          }
        />
      )}

      {showReport && (
        <>
          <BrazeContentCardWrapper>
            <BrazeContentCard
              feedType="company-overview.financial_health"
              anchorTarget="_blank"
            />
          </BrazeContentCardWrapper>
          <PageAnchor type="sub-section-title" id={HEALTH_FINANCIAL_POSITION} />
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title="Financial Position Analysis"
            trackingProps={{ subject: 'chart' }}
            body={
              <ChartWrapper
                id="health-financial-position-chart"
                preRenderHeight="358px"
                preRenderHeightMdDown="355px"
                chart={
                  <AssetsVsLiabilities
                    companyName={companyName}
                    shortTermAssets={shortTermAssets}
                    shortTermLiabilities={shortTermLiabilities}
                    longTermAssets={longTermAssets}
                    longTermLiabilities={longTermLiabilities}
                    shortTermAssetsFormatted0DP={shortTermAssetsFormatted0DP}
                    shortTermAssetsFormatted2DP={shortTermAssetsFormatted2DP}
                    shortTermLiabilitiesFormatted0DP={
                      shortTermLiabilitiesFormatted0DP
                    }
                    shortTermLiabilitiesFormatted2PD={
                      shortTermLiabilitiesFormatted2PD
                    }
                    longTermAssetsFormatted0DP={longTermAssetsFormatted0DP}
                    longTermAssetsFormatted2DP={longTermAssetsFormatted2DP}
                    longTermLiabilitiesFormatted0DP={
                      longTermLiabilitiesFormatted0DP
                    }
                    longTermLiabilitiesFormatted2DP={
                      longTermLiabilitiesFormatted2DP
                    }
                  />
                }
                feedbackLabel={COMPANY_FINANCIAL_POSITION_ANALYSIS}
                exportDarkUrl={netWorthDownloadUrl}
              />
            }
            statements={
              !isBank && (
                <>
                  <Statement
                    title={areShortTermLiabilitiesCovered?.title}
                    description={
                      <Description statement={areShortTermLiabilitiesCovered} />
                    }
                    value={areShortTermLiabilitiesCovered?.value}
                    statementId={CompanyStatements.healthStatement1}
                  />
                  <Statement
                    title={areLongTermLiabilitiesCovered?.title}
                    description={
                      <Description statement={areLongTermLiabilitiesCovered} />
                    }
                    value={areLongTermLiabilitiesCovered?.value}
                    statementId={CompanyStatements.healthStatement2}
                  />
                </>
              )
            }
          />
          <hr />
          <PageAnchor type="sub-section-title" id={HEALTH_DEBT_HISTORY} />
          <PageAnchor type="sub-section-title" id={LegacyAnchor.DEBT_EQUITY} />
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title="Debt to Equity History and Analysis"
            trackingProps={{ subject: 'chart' }}
            bodyRender={(title) => (
              <ChartWrapper
                id="health-debt-history-chart"
                preRenderHeight="543px"
                preRenderHeightSmDown="475px"
                chart={
                  <DebtToEquityHistory
                    cash={cash}
                    cashMax={cashMax}
                    cashMin={cashMin}
                    currencyISO={reportingCurrencyISO}
                    debt={debt}
                    debtMax={debtMax}
                    debtMin={debtMin}
                    equity={equity}
                    equityMax={equityMax}
                    equityMin={equityMin}
                    reportingUnit={reportingUnit}
                  />
                }
                rawDataModal={
                  <DebtToEquityHistoryRawDataContainer companyId={companyId} />
                }
                rawDataTitle={title}
                feedbackLabel={COMPANY_DEBT_TO_EQUITY_HISTORY}
                exportDarkUrl={historicalDebtDownloadUrl}
              />
            )}
            statements={
              !isBank && (
                <>
                  <Statement
                    title={isDebtLevelAppropriate?.title}
                    description={
                      <Description statement={isDebtLevelAppropriate} />
                    }
                    value={isDebtLevelAppropriate?.value}
                    statementId={CompanyStatements.healthStatement3}
                  />
                  <Statement
                    title={hasDebtReducedOverTime?.title}
                    description={
                      <Description statement={hasDebtReducedOverTime} />
                    }
                    value={hasDebtReducedOverTime?.value}
                    statementId={CompanyStatements.healthStatement4}
                  />
                  {!showCashRunwayAnalysis && (
                    <>
                      <Statement
                        title={isDebtCoveredByCashflow?.title}
                        description={
                          <Description statement={isDebtCoveredByCashflow} />
                        }
                        value={isDebtCoveredByCashflow?.value}
                        statementId={CompanyStatements.healthStatement5}
                      />
                      <Statement
                        title={isInterestCoveredByProfit?.title}
                        description={
                          <Description statement={isInterestCoveredByProfit} />
                        }
                        value={isInterestCoveredByProfit?.value}
                        statementId={CompanyStatements.healthStatement6}
                      />
                    </>
                  )}
                </>
              )
            }
          />
          <hr />
          <PageAnchor type="sub-section-title" id={HEALTH_BALANCE_SHEET} />
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title="Balance Sheet"
            body={
              <ChartWrapper
                id="health-balance-sheet-chart"
                preRenderHeight="315px"
                preRenderHeightMdDown="624px"
                chart={
                  <BalanceSheetWrapper>
                    <Column
                      col={12}
                      colLg={6}
                      data-cy-id="balance-sheet-assets"
                    >
                      <h4>Assets</h4>
                      <BalanceSheet data={assetsBalanceSheet} />
                    </Column>
                    <Column
                      col={12}
                      colLg={6}
                      data-cy-id="balance-sheet-liabilities"
                    >
                      <h4>Liabilities + Equity</h4>
                      <BalanceSheet data={liabilitiesBalanceSheet} />
                    </Column>
                  </BalanceSheetWrapper>
                }
              />
            }
          />
          {showCashRunwayAnalysis && (
            <>
              <hr />
              <PageAnchor type="sub-section-title" id={HEALTH_CASH_RUNWAY} />
              <ReportSubSection
                titleElement={subSectionTitleElement}
                title="Cash Runway Analysis"
                body={
                  <p>
                    For companies that have on average been loss making in the
                    past we assess whether they have at least 1 year of cash
                    runway.
                  </p>
                }
                statements={
                  <>
                    <Statement
                      title={hasCashRunwayIfStable?.title}
                      description={
                        <Description statement={hasCashRunwayIfStable} />
                      }
                      value={hasCashRunwayIfStable?.value}
                      statementId="hasCashRunwayIfStable"
                    />
                    <Statement
                      title={hasCashRunwayIfGrowing?.title}
                      description={
                        <Description statement={hasCashRunwayIfGrowing} />
                      }
                      value={hasCashRunwayIfGrowing?.value}
                      statementId="hasCashRunwayIfGrowing"
                    />
                  </>
                }
              />
            </>
          )}
          {isBank && (
            <>
              <hr />
              <PageAnchor
                type="sub-section-title"
                id={HEALTH_FINANCIAL_INSTITUTION}
              />
              <ReportSubSection
                titleElement={subSectionTitleElement}
                title="Financial Institutions Analysis"
                trackingProps={{ subject: 'chart' }}
                statements={
                  <>
                    <Statement
                      title={hasAnAppropriateLevelOfAssets?.title}
                      description={
                        <Description
                          statement={hasAnAppropriateLevelOfAssets}
                        />
                      }
                      value={hasAnAppropriateLevelOfAssets?.value}
                      statementId={
                        CompanyStatements.alternativeHealthStatement1
                      }
                    />
                    <Statement
                      title={hasAppropriateBadLoanAllowance?.title}
                      description={
                        <Description
                          statement={hasAppropriateBadLoanAllowance}
                        />
                      }
                      value={hasAppropriateBadLoanAllowance?.value}
                      statementId={
                        CompanyStatements.alternativeHealthStatement2
                      }
                    />
                    <Statement
                      title={hasPrimarilyLowRiskFunding?.title}
                      description={
                        <Description statement={hasPrimarilyLowRiskFunding} />
                      }
                      value={hasPrimarilyLowRiskFunding?.value}
                      statementId={
                        CompanyStatements.alternativeHealthStatement3
                      }
                    />
                    <Statement
                      title={hasAppropriateLoanLevel?.title}
                      description={
                        <Description statement={hasAppropriateLoanLevel} />
                      }
                      value={hasAppropriateLoanLevel?.value}
                      statementId={
                        CompanyStatements.alternativeHealthStatement4
                      }
                    />
                    <Statement
                      title={hasPrimarilyDepositFunding?.title}
                      description={
                        <Description statement={hasPrimarilyDepositFunding} />
                      }
                      value={hasPrimarilyDepositFunding?.value}
                      statementId={
                        CompanyStatements.alternativeHealthStatement5
                      }
                    />
                    <Statement
                      title={hasAppropriateNonPerformingLoans?.title}
                      description={
                        <Description
                          statement={hasAppropriateNonPerformingLoans}
                        />
                      }
                      value={hasAppropriateNonPerformingLoans?.value}
                      statementId={
                        CompanyStatements.alternativeHealthStatement6
                      }
                    />
                  </>
                }
              />
            </>
          )}
        </>
      )}
      {isFund === false && (
        <>
          <hr />
          {nextStep}
        </>
      )}
    </ReportSection>
  );
};

export default track({ type: 'track', subject: 'Health' })(Health);
